import { useEffect, useRef, useState, type FormEvent } from 'react';
import { api, errorText } from '../../api';
import { fieldId, SelectField, TextField, useForm, type FormErrors } from '../../auth/CompanyFields';
import { t, tl, tx } from '../../i18n';
import { fmtDate } from '../../lib/format';
import { useToast } from '../../lib/toast';
import { useApi } from '../../lib/useApi';
import { Preview } from '../belege/BelegSheet';
import type { EmployeeDetail } from './types';
import { ShareFile } from '../../components/ShareFile';

// „Kündigung erstellen“ in der Personalakte (Sam 11.10.): Der Mandant erstellt das Schreiben selbst aus der Vorlage
// (server/src/modules/lohn/kuendigung.ts), sieht es an und druckt es aus. Den Termin schlägt der Server nach § 622 BGB
// vor. Wirksam wird die Kündigung erst auf Papier mit Unterschrift von Hand. Mit dem Erstellen ist sie der Kanzlei auch
// gleich gemeldet (Sam 12.10., Server: kuendigung-routes.ts). Das Formular ist übersetzt, das Schreiben (PDF) bleibt Deutsch.

interface Vorschlag {
  today: string;
  maxZugang: string;
  entryDate: string;
  zugang: string;
  termin: string;
  regel: string;
  probezeitMoeglich: boolean;
  schwerbehindert: boolean;
  defaults: Record<'strasse' | 'plz' | 'ort' | 'unterzeichner' | 'funktion' | 'briefort', string>;
}

const ARTEN = [tl('Ordentliche Kündigung'), tl('Fristlose Kündigung')] as const;
const dateOk = (v: string) => /^\d{4}-\d{2}-\d{2}$/.test(v) && !Number.isNaN(Date.parse(v));

export function KuendigungForm({ e, onDone, onCancel }: { e: EmployeeDetail; onDone(): void; onCancel(): void }) {
  if (e.kind === 'Auszubildende/r') return <AzubiHinweis onCancel={onCancel} />;
  return <Laden e={e} onDone={onDone} onCancel={onCancel} />;
}

// Für Auszubildende gibt es kein Schreiben aus der App (§ 22 BBiG, Gesetzes-Prüfung A1, Sam 13.10.), der Server lehnt
// es auch ab. „Kündigung melden“ bleibt.
function AzubiHinweis({ onCancel }: { onCancel(): void }) {
  return (
    <div className="kdg">
      <p className="kdg-warn">
        {t(
          'Für Auszubildende gelten bei der Kündigung eigene Regeln, deshalb erstellt die App hier kein Schreiben. Bitte fragen Sie Ihre Kammer oder einen Anwalt. Danach melden Sie die Kündigung wie gewohnt über „Kündigung melden“.',
        )}
      </p>
      <div className="row">
        <button className="btn2" type="button" onClick={onCancel}>
          {t('Zurück')}
        </button>
      </div>
    </div>
  );
}

function Laden({ e, onDone, onCancel }: { e: EmployeeDetail; onDone(): void; onCancel(): void }) {
  const base = useApi<Vorschlag>(`/api/app/employee/${e.id}/kuendigung`);
  const v0 = base.data;
  if (!v0) return base.error ? <p className="meta">{base.error}</p> : <div className="spinner" aria-label={t('Lädt')} />;
  return <Form e={e} v0={v0} onDone={onDone} onCancel={onCancel} />;
}

function Form({ e, v0, onDone, onCancel }: { e: EmployeeDetail; v0: Vorschlag; onDone(): void; onCancel(): void }) {
  const toast = useToast();
  const f = useForm({ art: ARTEN[0], zugang: v0.zugang, termin: v0.termin, ...v0.defaults });
  const [probezeit, setProbezeit] = useState(false);
  const [freistellung, setFreistellung] = useState(false);
  const [rueckgabe, setRueckgabe] = useState(true);
  const [s, setS] = useState({ termin: v0.termin, regel: v0.regel, probezeitMoeglich: v0.probezeitMoeglich });
  // von Hand geändertes Ende bleibt, auch wenn sich die Übergabe ändert
  const edited = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // gemeldet: letzter Arbeitstag, wenn die Kündigung dabei der Kanzlei gemeldet wurde
  const [pdf, setPdf] = useState<{ url: string; name: string; gemeldet: string | null } | null>(null);
  const val = (k: string) => (f.values[k] ?? '').trim();
  const fristlos = f.values.art === ARTEN[1];
  const zugang = val('zugang');
  const pz = probezeit && s.probezeitMoeglich;

  // Vorschlag für das Ende neu holen, wenn sich Übergabe oder Probezeit ändern
  useEffect(() => {
    if (!dateOk(zugang)) return;
    let gone = false;
    const timer = setTimeout(() => {
      api
        .get<Vorschlag>(`/api/app/employee/${e.id}/kuendigung?zugang=${zugang}&probezeit=${probezeit ? 1 : 0}`)
        .then((r) => {
          if (gone) return;
          setS({ termin: r.termin, regel: r.regel, probezeitMoeglich: r.probezeitMoeglich });
          if (!edited.current) f.set('termin', r.termin);
        })
        .catch(() => {});
    }, 250);
    return () => {
      gone = true;
      clearTimeout(timer);
    };
    // f.set ist stabil
  }, [e.id, zugang, probezeit]);

  async function submit(ev: FormEvent) {
    ev.preventDefault();
    setError(null);
    const errors: FormErrors = {};
    if (!dateOk(zugang)) errors.zugang = t('Bitte angeben, wann der Mitarbeiter die Kündigung bekommt.');
    else if (zugang < v0.today) errors.zugang = t('Die Übergabe kann nicht vor heute liegen.');
    if (!dateOk(val('termin'))) errors.termin = t('Bitte das Ende prüfen.');
    else if (dateOk(zugang) && val('termin') < zugang) errors.termin = t('Die Kündigung kann nicht vor der Übergabe enden.');
    if (!val('unterzeichner')) errors.unterzeichner = t('Bitte angeben, wer unterschreibt.');
    if (!val('briefort')) errors.briefort = t('Bitte den Ort angeben.');
    const order = ['zugang', 'termin', 'unterzeichner', 'briefort'];
    const first = order.find((k) => errors[k]);
    if (first) {
      f.setErrors(errors);
      document.getElementById(fieldId(first))?.focus();
      toast(errors[first]!);
      return;
    }
    setBusy(true);
    try {
      // Das Schreiben liegt danach kurz unter einer Adresse auf dem Server (ansehen, herunterladen, auch in der App)
      const r = await api.post<{ url: string; name: string; gemeldet: string | null }>(`/api/app/employee/${e.id}/kuendigung`, {
        art: fristlos ? 'fristlos' : 'ordentlich',
        probezeit: !fristlos && pz,
        zugang,
        termin: val('termin'),
        freistellung: !fristlos && freistellung,
        rueckgabe,
        strasse: val('strasse'),
        plz: val('plz'),
        ort: val('ort'),
        unterzeichner: val('unterzeichner'),
        funktion: val('funktion'),
        briefort: val('briefort'),
      });
      setPdf(r);
      document.querySelector('.mapp .sheet')?.scrollTo({ top: 0 });
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  if (pdf) {
    return (
      <div className="kdg">
        {pdf.gemeldet && (
          <p className="kdg-ok" role="status">
            ✓ {t('Ihre Kanzlei ist informiert: Kündigung gemeldet, letzter Arbeitstag {date}.', { date: fmtDate(pdf.gemeldet) })}
          </p>
        )}
        <Preview url={pdf.url} mime="application/pdf" name={pdf.name} />
        <div className="row">
          <a className="btn2 dark" href={`${pdf.url}?download=1`} download={pdf.name}>
            {t('PDF herunterladen')}
          </a>
          <ShareFile url={`${pdf.url}?download=1`} name={pdf.name} />
          <button className="btn2" type="button" onClick={() => setPdf(null)}>
            {t('Ändern')}
          </button>
        </div>
        <ol className="kdg-steps">
          <li>{t('Ausdrucken und von Hand unterschreiben.')}</li>
          <li>
            {t(
              'Dem Mitarbeiter am {date} im Original übergeben und den Empfang unten auf dem Blatt unterschreiben lassen. Geht das nicht, per Bote mit Zeugen zustellen.',
              { date: fmtDate(zugang) },
            )}
          </li>
          {pdf.gemeldet && <li>{t('Ihre Kanzlei meldet den Mitarbeiter zum letzten Arbeitstag ab.')}</li>}
        </ol>
        <div className="row">
          <button className="btn2" type="button" onClick={onDone}>
            {t('Fertig')}
          </button>
        </div>
      </div>
    );
  }

  const early = dateOk(val('termin')) && val('termin') < s.termin;
  return (
    <form className="form maform mform kdg" onSubmit={submit} noValidate>
      <SelectField f={f} name="art" label={t('Art der Kündigung')} required options={ARTEN} />
      {fristlos && (
        <small className="fh warn">
          {t(
            'Fristlos nur bei einem wichtigen Grund (z. B. Diebstahl, Arbeitsverweigerung) und innerhalb von 2 Wochen, nachdem Sie davon erfahren haben (§ 626 BGB). Meist muss vorher eine Abmahnung erfolgt sein. Fragen Sie am besten vorher einen Anwalt.',
          )}
        </small>
      )}
      <div className="two">
        <TextField
          f={f}
          name="zugang"
          label={t('Übergabe an den Mitarbeiter am')}
          required
          type="date"
          min={v0.today}
          max={v0.maxZugang}
        />
        <TextField
          f={f}
          name="termin"
          label={fristlos ? t('Hilfsweise ordentlich zum') : t('Kündigung zum')}
          required
          type="date"
          min={zugang || v0.today}
          onInput={() => (edited.current = true)}
        />
      </div>
      {!fristlos && s.probezeitMoeglich && (
        <label className="chk">
          <input type="checkbox" checked={probezeit} onChange={(ev) => setProbezeit(ev.target.checked)} />
          <span>{t('Im Arbeitsvertrag ist eine Probezeit vereinbart (Frist dann 2 Wochen)')}</span>
        </label>
      )}
      <small className="fh">
        {t(
          'Gesetzliche Frist: {rule}, also frühestens zum {date}. Steht im Arbeitsvertrag oder Tarifvertrag (z. B. Bau) eine längere Frist, gilt diese.',
          { rule: tx(s.regel), date: fmtDate(s.termin) },
        )}
      </small>
      {early && (
        <small className="fh warn">
          {t('Das ist früher als die gesetzliche Frist. Das Arbeitsverhältnis endet dann trotzdem erst zum {date}.', {
            date: fmtDate(s.termin),
          })}
        </small>
      )}
      {v0.schwerbehindert && (
        <small className="fh warn">
          {t(
            'Der Mitarbeiter ist schwerbehindert: Vor der Kündigung muss das Integrationsamt zustimmen (§ 168 SGB IX), sonst ist sie unwirksam.',
          )}
        </small>
      )}
      {!fristlos && (
        <label className="chk">
          <input type="checkbox" checked={freistellung} onChange={(ev) => setFreistellung(ev.target.checked)} />
          <span>{t('Bis zum Ende freistellen (bezahlt, Resturlaub wird angerechnet)')}</span>
        </label>
      )}
      <label className="chk">
        <input type="checkbox" checked={rueckgabe} onChange={(ev) => setRueckgabe(ev.target.checked)} />
        <span>{t('Rückgabe von Schlüsseln, Arbeitskleidung, Werkzeug und Firmenhandy verlangen')}</span>
      </label>
      <fieldset>
        <legend>{t('Anschrift von {name}', { name: e.name })}</legend>
        <TextField f={f} name="strasse" label={t('Straße und Hausnummer')} maxLength={120} autoComplete="off" />
        <div className="two">
          <TextField f={f} name="plz" label={t('PLZ')} maxLength={10} inputMode="numeric" autoComplete="off" />
          <TextField f={f} name="ort" label={t('Ort')} maxLength={80} autoComplete="off" />
        </div>
      </fieldset>
      <fieldset>
        <legend>{t('Unterschrift')}</legend>
        <div className="two">
          <TextField f={f} name="unterzeichner" label={t('Unterschrift von')} required maxLength={100} autoComplete="name" />
          <TextField f={f} name="funktion" label={t('Funktion')} maxLength={60} placeholder={t('z. B. Geschäftsführung')} />
        </div>
        <TextField f={f} name="briefort" label={t('Ort des Schreibens')} required maxLength={80} />
      </fieldset>
      {!e.termination && (
        <small className="fh">{t('Beim Erstellen wird die Kündigung auch gleich Ihrer Kanzlei gemeldet.')}</small>
      )}
      {error && (
        <p className="formerr" role="alert">
          {error}
        </p>
      )}
      <div className="row">
        <button className="btn2 dark" type="submit" disabled={busy}>
          {busy ? t('Einen Moment …') : t('Kündigung erstellen (PDF)')}
        </button>
        <button className="btn2" type="button" onClick={onCancel}>
          {t('Abbrechen')}
        </button>
      </div>
      <div className="src">
        {t(
          'Gültig ist die Kündigung nur auf Papier mit Ihrer Unterschrift von Hand (Inhaber, Geschäftsführung oder Prokura). E-Mail, WhatsApp oder eine Unterschrift in der App reichen nicht (§ 623 BGB). Bei Schwangerschaft, Elternzeit, Schwerbehinderung oder Betriebsrat gilt ein besonderer Schutz, bei mehr als 10 Mitarbeitern das Kündigungsschutzgesetz. Fragen Sie im Zweifel vorher einen Anwalt, diese Vorlage ersetzt keine Rechtsberatung.',
        )}
      </div>
    </form>
  );
}
